import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

let globalUnsavedChanges = false;
let globalNavigationCallback: ((path: string) => void) | null = null;

export const setGlobalUnsavedChanges = (hasChanges: boolean) => {
  globalUnsavedChanges = hasChanges;
};

export const setGlobalNavigationCallback = (callback: ((path: string) => void) | null) => {
  globalNavigationCallback = callback;
};

export const checkUnsavedChanges = (path: string) => {
  if (globalUnsavedChanges && globalNavigationCallback) {
    globalNavigationCallback(path);
    return false;
  }
  return true;
};

const useUnsavedChanges = ({ isDirty }: { isDirty: boolean }) => {
  const history = useHistory();
  // const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isNavigating, setIsNavigating] = useState(false);
  const hasAddedHistoryEntry = useRef(false);
  const historyIndexRef = useRef(0);

  const handleBeforeUnload = (event: any) => {
    if (isDirty && !isNavigating) {
      event.preventDefault();
      return '';
    }
  };

  const handlePopState = (event: PopStateEvent) => {
    if (isNavigating) {
      return; // Allow navigation if we're in the process of navigating
    }
    if (isDirty) {
      // Prevent the navigation
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setPendingNavigation('BACK');
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    if (isDirty && !hasAddedHistoryEntry.current) {
      // Add history entry only once when form becomes dirty
      window.history.pushState(null, '', window.location.href);
      hasAddedHistoryEntry.current = true;
      historyIndexRef.current = window.history.length;
    } else if (!isDirty && hasAddedHistoryEntry.current) {
      // Reset when form is no longer dirty
      hasAddedHistoryEntry.current = false;
    }
  }, [isDirty]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isDirty, isNavigating]);

  useEffect(() => {
    setGlobalUnsavedChanges(isDirty);
    setGlobalNavigationCallback((path: string) => {
      setPendingNavigation(path);
      setIsModalOpen(true);
    });

    return () => {
      setGlobalUnsavedChanges(false);
      setGlobalNavigationCallback(null);
    };
  }, [isDirty]);

  const handleNavigationAttempt = (navigateTo: any) => {
    if (isDirty) {
      setPendingNavigation(navigateTo);
      setIsModalOpen(true);
    } else if (navigateTo === -1) {
        history.goBack();
      } else {
        history.push(navigateTo);
      }
  };

  const handleConfirmNavigation = (navigateTo?: any) => {
    const targetPath = navigateTo || pendingNavigation;
    setIsModalOpen(false);
    setIsNavigating(true);
    // Small delay to ensure modal closes and state updates
    setTimeout(() => {
      if (targetPath === 'BACK') {
        // Go back 2 steps to account for the extra history entry we added
        window.history.go(-2);
      } else {
        history.push(targetPath);
      }
      // Reset navigation state after a short delay
      setTimeout(() => {
        setIsNavigating(false);
        setPendingNavigation(null);
      }, 100);
    }, 50);
  };

  const handleCancelNavigation = () => {
    setIsModalOpen(false);
    setPendingNavigation(null);
  };

  return {
    isModalOpen,
    setIsModalOpen,
    handleNavigationAttempt,
    handleConfirmNavigation,
    handleCancelNavigation,
    pendingNavigation,
  };
};

export default useUnsavedChanges;
